import { motion } from 'framer-motion'
import { ContactPlantScene } from './ContactPlantScene'
import { useState, useEffect } from 'react'

interface ContactProps {
  isMobile?: boolean
}

export function Contact({ isMobile = false }: ContactProps) {
  // Su mobile: colore fisso per evitare problemi di performance
  const [gradientColor, setGradientColor] = useState(isMobile ? '#7ec876' : '#7ec876')
  const [mouseX, setMouseX] = useState(0)
  const [mouseY, setMouseY] = useState(0)

  useEffect(() => {
    if (isMobile) {
      // Su mobile: nessun cambio colore dinamico
      return
    }

    const colors = [
      '#7ec876', // verde originale
      '#4da6ff', // blu
      '#ff6b6b', // rosso
      '#ffd93d', // giallo
      '#a78bfa', // viola
      '#f472b6', // rosa
      '#34d399', // verde menta
      '#f59e0b'  // arancione
    ]

    const interval = setInterval(() => {
      const randomColor = colors[Math.floor(Math.random() * colors.length)]
      setGradientColor(randomColor)
    }, 3333)

    return () => clearInterval(interval)
  }, [isMobile])

  useEffect(() => {
    if (isMobile) return

    const handleMouseMove = (e: MouseEvent) => {
      setMouseX(e.clientX)
      setMouseY(e.clientY)
    }

    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [isMobile])

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        damping: 12,
        stiffness: 100
      }
    }
  }

  return (
    <section id="contact" className="py-12 md:py-20 z-20 relative" style={{ backgroundColor: '#CFCFCF' }}>
      <div className="container mx-auto px-4">
        {/* NO title - immediately visible content */}

        {/* Grid Layout - Three Columns */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: isMobile ? 0.3 : 0.8, delay: 0.1, ease: "easeOut" }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12 md:mb-16"
        >
          {/* Left Column - Contact Info */}
          <div className="lg:col-span-1">
            <p className="text-xs md:text-sm text-[#444444] leading-relaxed mb-6 md:mb-8">
              Ready to bring your vision to life? I'm always excited to collaborate on 
              innovative projects that push creative boundaries and deliver exceptional results.
            </p>
            
            {/* Contact Details */}
            <div className="space-y-4 md:space-y-6">
              <div>
                <h3 className="text-base md:text-lg font-bold text-[#444444] mb-2">Email</h3>
                <a href="mailto:hello@danielegrieco.com" className="text-[#444444]/70 hover:text-[#444444] transition-colors text-sm md:text-base">
                  daniele.grieco.b@gmail.com
                </a>
              </div>
              
              <div>
                <h3 className="text-base md:text-lg font-bold text-[#444444] mb-2">Location</h3>
                <p className="text-[#444444]/70 text-sm md:text-base">Genoa, Italy</p>
              </div>
              
              <div>
                <h3 className="text-base md:text-lg font-bold text-[#444444] mb-2">Available For</h3>
                <ul className="text-[#444444]/70 space-y-1 text-sm md:text-base">
                  <li>• Freelance Projects</li>
                  <li>• On-site meetings</li>
                  <li>• Consultancy Work</li>
                  <li>• Collaborative Ventures</li>
                </ul>
              </div>
            </div>

            {/* Social Links */}
            <div className="mt-6 md:mt-8 flex space-x-3 md:space-x-4">
              <a href="https://www.linkedin.com/in/daniele-grieco/" className="text-[#444444]/50 hover:text-[#444444] transition-colors text-xs md:text-sm">
                LinkedIn
              </a>
              <a href="#" className="text-[#444444]/50 hover:text-[#444444] transition-colors text-xs md:text-sm">
                Behance
              </a>
              <a href="#" className="text-[#444444]/50 hover:text-[#444444] transition-colors text-xs md:text-sm">
                Instagram
              </a>
            </div>
          </div>

          {/* Middle + Right Columns - 3D Plant Scene (2 columns) */}
          <div className="lg:col-span-2 relative flex items-center justify-center">
            {/* Gradiente nella colonna di destra, contenuto all'interno di Contact */}
            <div 
              className="absolute right-0 top-0 bottom-0 w-1/2 pointer-events-none z-5"
              style={{
                background: `linear-gradient(to top, ${gradientColor} 0%, ${gradientColor} 85%, transparent 100%)`
              }}
            ></div>
            
            {/* Desktop: mostra la pianta 3D. Mobile: mostra un'immagine statica */}
            {isMobile ? (
              <div className="w-full h-64 md:h-96 flex items-center justify-center">
                <img 
                  src="/IMG/plant_static.png" 
                  alt="Plant" 
                  className="w-48 h-48 object-contain opacity-80"
                  onError={(e) => {
                    // Fallback if image doesn't exist
                    e.currentTarget.style.display = 'none'
                  }}
                />
              </div>
            ) : (
              <ContactPlantScene mouseX={mouseX} mouseY={mouseY} />
            )}
          </div>

        </motion.div>

        {/* Closing Text */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: isMobile ? 0.3 : 0.8, delay: 0.4, ease: "easeOut" }}
          className="text-center"
        >
          <p className="text-xs md:text-sm text-[#444444]">
            Looking forward to hearing from you and discussing how we can create something amazing together.
          </p>
        </motion.div>
      </div>
    </section>
  )
}
